/**
 * railStatus.ts
 *
 * Pure helpers for the Workspace Rail (LoanRail for DD, DocumentRail for BA):
 * the one plain-language status line per loan, the standard borrower document
 * rows (including missing ones), the heading count notes and the
 * "n of m confirmed" summaries read from the saved confirmations.
 *
 * No React and no network.
 *
 * Reference: prototype `ddRail`, `baRail`, `railNote`.
 * Requirement: 3.1.
 */

import { LoanStatus } from '../graphql/types';
import type { Document, Loan } from '../graphql/types';
import type { StatusDotTone } from '../components/ui/StatusDot';
import type { AnalyzeResult } from './analyze/analyzeModel';
import {
  countConfirmed,
  groupExtractFields,
  loadConfirmations,
} from './extract/extractModel';
import type { ConfirmationOverrides, ExtractField } from './extract/extractModel';
import { BA_CHECKLIST, matchesChecklistItem } from './intake/intakeBAModel';
import type { ChecklistItem } from './intake/intakeBAModel';

// ── Shared types ────────────────────────────────────────────────────────────

export interface ConfirmationSummary {
  confirmed: number;
  total: number;
}

export function formatConfirmed(summary: ConfirmationSummary): string {
  return `${summary.confirmed} of ${summary.total} confirmed`;
}

export interface RailStatusLine {
  /** One plain-language line, for example "Blocked · missing Final HUD-1". */
  line: string;
  dot: StatusDotTone;
  /** True when the row waits on the reviewer; counted in the heading note. */
  needsYou: boolean;
}

// ── DD: loan status line ────────────────────────────────────────────────────

export interface LoanStatusInput {
  loan: Pick<Loan, 'loanId' | 'status' | 'failedFileCount'>;
  /**
   * Stored completeness, consistency and guideline findings for the job
   * (`buildAnalyze`). Null or absent when no run result is known.
   */
  analysis?: AnalyzeResult | null;
  /** Fields read with low confidence for this loan, where known. */
  lowConfidenceCount?: number;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** "Missing: Final HUD-1" → "Blocked · missing Final HUD-1". */
function blockedLine(reasons: ReadonlyArray<string>): string {
  const first = reasons[0] ?? '';
  const missing = /^Missing:\s*(.+)$/i.exec(first);
  const notMet = /^Not met:\s*(.+)$/i.exec(first);
  let text: string;
  if (missing) text = `missing ${missing[1]}`;
  else if (notMet) text = `${notMet[1]} not met`;
  else text = first || 'required documents are incomplete';
  const more = reasons.length - 1;
  return `Blocked · ${text}${more > 0 ? ` (+${more} more)` : ''}`;
}

function consistencyLine(titles: ReadonlyArray<string>): string {
  if (titles.length === 1) {
    return /\bname/i.test(titles[0]) ? 'Name conflict across docs' : 'Conflict across docs';
  }
  return `${titles.length} conflicts across docs`;
}

const GUIDELINE_PROBLEM_LINES: Readonly<Record<string, string>> = {
  FAIL: 'Failed guideline review',
  WARNING: 'Guideline warning',
  MISSING_DATA: 'Guideline data missing',
  ERROR: 'Guideline review error',
};

/**
 * The one status line for a DD loan, derived from the stored findings.
 *
 * Order, most blocking first: failed files, missing required documents,
 * consistency conflict, guideline problem, low-confidence field, guideline
 * review pending, all checks passed. Without run results it falls back to the
 * loan's own status.
 *
 * Whole-job findings that cannot be tied to one loan (`loanId` null) are not
 * attributed to any loan here.
 */
export function getLoanRailStatus(input: LoanStatusInput): RailStatusLine {
  const { loan, analysis, lowConfidenceCount = 0 } = input;

  if (loan.failedFileCount > 0) {
    const n = loan.failedFileCount;
    return {
      line: `${n} ${plural(n, 'file', 'files')} failed to extract`,
      dot: 'danger',
      needsYou: true,
    };
  }

  const held = analysis?.heldBack.find((h) => h.loanId === loan.loanId);
  if (held) {
    return { line: blockedLine(held.reasons), dot: 'danger', needsYou: true };
  }

  const own = (analysis?.findings ?? []).filter((f) => f.loanId === loan.loanId);

  const conflicts = own.filter((f) => f.kind === 'consistency' && f.status !== 'PASS');
  if (conflicts.length > 0) {
    return {
      line: consistencyLine(conflicts.map((f) => f.title)),
      dot: 'warning',
      needsYou: true,
    };
  }

  const guideline = own.find((f) => f.kind === 'guideline');
  if (guideline && guideline.status !== 'PASS') {
    return {
      line: GUIDELINE_PROBLEM_LINES[guideline.status] ?? 'Guideline review needs attention',
      dot: guideline.status === 'FAIL' ? 'danger' : 'warning',
      needsYou: true,
    };
  }

  if (lowConfidenceCount > 0) {
    return {
      line:
        lowConfidenceCount === 1
          ? 'Low-confidence field'
          : `${lowConfidenceCount} low-confidence fields`,
      dot: 'warning',
      needsYou: true,
    };
  }

  if (analysis?.ran.completeness) {
    if (guideline) return { line: 'All checks passed', dot: 'success', needsYou: false };
    return { line: 'Guideline review pending', dot: 'idle', needsYou: false };
  }

  switch (loan.status) {
    case LoanStatus.PROCESSING:
      return { line: 'Extracting', dot: 'running', needsYou: false };
    case LoanStatus.REVIEW_REQUIRED:
      return { line: 'Needs review', dot: 'warning', needsYou: true };
    case LoanStatus.REVIEW_COMPLETED:
      return { line: 'Reviewed', dot: 'success', needsYou: false };
    default:
      return { line: 'Not started', dot: 'idle', needsYou: false };
  }
}

/** Heading note for the Loans Rail: "3 need you", or '' when none. */
export function loanRailNote(statuses: ReadonlyArray<Pick<RailStatusLine, 'needsYou'>>): string {
  const n = statuses.filter((s) => s.needsYou).length;
  return n > 0 ? `${n} need you` : '';
}

// ── BA: document rows ───────────────────────────────────────────────────────

export type DocumentRowState = 'extracted' | 'conflict' | 'review' | 'failed' | 'missing';

export interface DocumentRailRow {
  key: string;
  /** Null for a standard document that has not been received. */
  documentId: string | null;
  title: string;
  /** "3 pages · extracted", "1 page · name conflict" or "Missing". */
  meta: string;
  dot: StatusDotTone;
  state: DocumentRowState;
  /** First field of the document, selected together with the document. */
  firstFieldId: string | null;
}

export interface DocumentRowsOptions {
  /** Standard document list; defaults to the borrower checklist. */
  checklist?: ReadonlyArray<ChecklistItem>;
  /** Conflict notes by document id that override those found in the fields. */
  conflicts?: Readonly<Record<string, string>>;
}

function startPage(pageRange: string | undefined): number {
  const n = parseInt(pageRange?.split('-')[0] || '1', 10);
  return Number.isFinite(n) ? n : 1;
}

/** Number of pages in a range such as "4-6", "7" or "1-2,5"; null when unknown. */
export function countPages(pageRange: string | undefined | null): number | null {
  if (!pageRange) return null;
  let total = 0;
  for (const part of pageRange.split(',')) {
    const m = /^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/.exec(part);
    if (!m) return null;
    const from = parseInt(m[1], 10);
    const to = m[2] ? parseInt(m[2], 10) : from;
    if (to < from) return null;
    total += to - from + 1;
  }
  return total > 0 ? total : null;
}

export function formatPages(pageRange: string | undefined | null): string | null {
  const n = countPages(pageRange);
  return n === null ? null : `${n} ${plural(n, 'page', 'pages')}`;
}

/** "Bank statements (2 months)" → "Bank statements". */
export function railChecklistLabel(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, '').trim() || label;
}

function documentTitle(doc: Pick<Document, 'documentType' | 'sourceFilename'>): string {
  return doc.documentType?.trim() || doc.sourceFilename?.trim() || 'Document';
}

function conflictLabel(fields: ReadonlyArray<ExtractField>): string | null {
  const conflicting = fields.filter((f) => f.conflictNote);
  if (conflicting.length === 0) return null;
  if (conflicting.length === 1) return `${conflicting[0].label} conflict`;
  return `${conflicting.length} field conflicts`;
}

function receivedRow(
  doc: Document,
  fields: ReadonlyArray<ExtractField>,
  conflict: string | null
): DocumentRailRow {
  let state: DocumentRowState = 'extracted';
  let tail = 'extracted';
  let dot: StatusDotTone = 'success';
  if (doc.errorMessage) {
    state = 'failed';
    tail = 'failed';
    dot = 'danger';
  } else if (conflict) {
    state = 'conflict';
    tail = conflict;
    dot = 'warning';
  } else if (doc.requiresHumanReview) {
    state = 'review';
    tail = 'needs review';
    dot = 'warning';
  }
  const pages = formatPages(doc.pageRange);
  return {
    key: doc.documentId,
    documentId: doc.documentId,
    title: documentTitle(doc),
    meta: [pages, tail].filter(Boolean).join(' · '),
    dot,
    state,
    firstFieldId: fields[0]?.id ?? null,
  };
}

/**
 * The standard borrower documents, in checklist order, with received documents
 * as selectable rows and missing ones as "Missing" rows. Documents that match
 * no standard entry follow, in page order.
 */
export function buildDocumentRailRows(
  documents: ReadonlyArray<Document>,
  options: DocumentRowsOptions = {}
): DocumentRailRow[] {
  const checklist = options.checklist ?? BA_CHECKLIST;
  const groups = groupExtractFields(documents);
  const fieldsByDoc = new Map(groups.map((g) => [g.documentId, g.fields]));
  const ordered = [...documents].sort((a, b) => startPage(a.pageRange) - startPage(b.pageRange));

  const rowFor = (doc: Document): DocumentRailRow => {
    const fields = fieldsByDoc.get(doc.documentId) ?? [];
    const conflict = options.conflicts?.[doc.documentId] ?? conflictLabel(fields);
    return receivedRow(doc, fields, conflict);
  };

  const used = new Set<string>();
  const rows: DocumentRailRow[] = [];
  for (const item of checklist) {
    const matches = ordered.filter(
      (doc) =>
        !used.has(doc.documentId) &&
        matchesChecklistItem(item, doc.documentType?.trim() || doc.sourceFilename || '')
    );
    if (matches.length === 0) {
      rows.push({
        key: `missing:${item.id}`,
        documentId: null,
        title: railChecklistLabel(item.label),
        meta: 'Missing',
        dot: 'danger',
        state: 'missing',
        firstFieldId: null,
      });
      continue;
    }
    for (const doc of matches) {
      used.add(doc.documentId);
      rows.push(rowFor(doc));
    }
  }
  for (const doc of ordered) {
    if (!used.has(doc.documentId)) rows.push(rowFor(doc));
  }
  return rows;
}

/** Heading note for the Documents Rail: "1 missing", else "n need you", else ''. */
export function documentRailNote(rows: ReadonlyArray<Pick<DocumentRailRow, 'state'>>): string {
  const missing = rows.filter((r) => r.state === 'missing').length;
  if (missing > 0) return `${missing} missing`;
  const attention = rows.filter((r) => r.state !== 'extracted').length;
  return attention > 0 ? `${attention} need you` : '';
}

// ── Confirmations from the saved overrides ──────────────────────────────────

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/** "n of m confirmed" per document, from already loaded overrides. */
export function summarizeDocumentConfirmations(
  documents: ReadonlyArray<Document>,
  overrides: ConfirmationOverrides
): Record<string, ConfirmationSummary> {
  const result: Record<string, ConfirmationSummary> = {};
  for (const group of groupExtractFields(documents)) {
    result[group.documentId] = {
      confirmed: countConfirmed(group.fields, overrides),
      total: group.fields.length,
    };
  }
  return result;
}

/** Per-document confirmations for one loan, read from its saved confirmations. */
export function documentConfirmationsFromSaved(
  jobId: string,
  loanId: string,
  documents: ReadonlyArray<Document>,
  storage?: StorageLike | null
): Record<string, ConfirmationSummary> {
  // An undefined `storage` falls through to the browser's localStorage.
  const overrides = loadConfirmations(jobId, loanId, storage);
  return summarizeDocumentConfirmations(documents, overrides);
}

/** Per-loan confirmations, summed over each loan's documents. */
export function loanConfirmationsFromSaved(
  jobId: string,
  documentsByLoan: Readonly<Record<string, ReadonlyArray<Document>>>,
  storage?: StorageLike | null
): Record<string, ConfirmationSummary> {
  const result: Record<string, ConfirmationSummary> = {};
  for (const [loanId, documents] of Object.entries(documentsByLoan)) {
    const perDoc = documentConfirmationsFromSaved(jobId, loanId, documents, storage);
    const sum = Object.values(perDoc).reduce(
      (acc, s) => ({ confirmed: acc.confirmed + s.confirmed, total: acc.total + s.total }),
      { confirmed: 0, total: 0 }
    );
    result[loanId] = sum;
  }
  return result;
}
